/*!
 * SkyPlus Contract Details Component
 * UPDATED: 
 *   - startReviewWorkflow(): shows "Workflow Started" toast + error overlay
 *   - pdfBlobUrl loading for PDF iframe 401 fix
 *   - isStartingWorkflow flag to prevent double-submit
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthenticationService } from '@alfresco/adf-core';
import { ContractService, ContractDetails } from './contract.service';
import { ContractWorkflowService } from '../contract-workflow/contract-workflow.service';
import { ContractInvoiceService, InvoiceResponse, InvoiceSummary, InvoiceRequest } from './contract-invoice.service';
import * as mammoth from 'mammoth';


@Component({
  selector: 'app-contract-details',
  templateUrl: './contract-details.component.html',
  styleUrls: ['./contract-details.component.scss']
})
export class ContractDetailsComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  // Contract Data
  contractId: string;
  contract: ContractDetails;

  // Preview — mammoth.js DOCX viewer
  mammothHtml: SafeHtml = null;
  mammothMessages: string[] = [];

  // Preview — PDF blob URL for iframe (used when uploaded file is a PDF)
  pdfBlobUrl: SafeResourceUrl | null = null;
  private _pdfObjectUrl: string | null = null;  // raw URL kept for revocation on destroy

  // State
  isLoading = true;
  isPreviewLoading = false;
  errorMessage = '';
  previewError = '';

  // Edit Properties
  isEditingProperties = false;
  isSavingProperties = false;
  saveError = '';
  editableContract: Partial<ContractDetails> = {};

  // UI toggles
  showDownloadMenu = false;
  showWorkflowConfirm = false;

  // ── WORKFLOW STATE (new) ─────────────────────────────────────────────────
  isStartingWorkflow = false;   // prevents double-click
  workflowSuccessMessage = '';  // shown in success toast
  workflowErrorMessage = '';    // shown in error banner
  // ─────────────────────────────────────────────────────────────────────────

  // ── INVOICE TAB STATE ────────────────────────────────────────────────────
  activeTab: 'document' | 'invoices' = 'document';

  invoices: InvoiceResponse[] = [];
  invoiceSummary: InvoiceSummary | null = null;
  isLoadingInvoices = false;
  invoiceError = '';

  // Add-invoice dialog
  showAddInvoice = false;
  isSavingInvoice = false;
  invoiceFormError = '';
  editingInvoiceId: number | null = null;

  invoiceForm: InvoiceRequest = {
    invoiceNumber:  '',
    invoiceDate:    '',
    invoiceAmount:  null,
    currency:       'KES',
    description:    '',
    supplierName:   '',
    poReference:    '',
    invoiceStatus:  'PENDING',
  };

  readonly invoiceStatuses = ['PENDING', 'APPROVED', 'PAID', 'REJECTED'];
  readonly invoiceCurrencies = ['KES', 'USD', 'EUR', 'GBP', 'UGX', 'TZS'];

  // Pending file attachment for an invoice (optional)
  pendingInvoiceFile: File | null = null;
  // ─────────────────────────────────────────────────────────────────────────

  // Lookup lists for edit form
  readonly contractTypes = [
    'Service Agreement', 'Supply Agreement', 'NDA', 'Lease', 'Employment',
    'Maintenance', 'Software License', 'Consultancy', 'Construction', 'Other'
  ];
  readonly contractStatuses = [
    'Draft', 'Pending Review', 'Active', 'Expiring Soon', 'Expired', 'Terminated'
  ];

  private alfrescoBaseUrl = 'http://localhost:8080/alfresco';
  metadataCollapsed = false;
  zoomLevel = 1;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private authService: AuthenticationService,
    private contractService: ContractService,
    private workflowService: ContractWorkflowService,
    private invoiceService: ContractInvoiceService
  ) {}

  ngOnInit(): void {
    document.addEventListener('click', this.handleDocumentClick);
    this.contractId = this.route.snapshot.paramMap.get('nodeId');
    if (this.contractId) {
      this.loadContract();
    } else {
      this.errorMessage = 'No contract ID provided';
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.handleDocumentClick);
    this.destroy$.next();
    this.destroy$.complete();
    if (this._pdfObjectUrl) {
      URL.revokeObjectURL(this._pdfObjectUrl);
    }
  }

  // ========== DATA LOADING ==========

  loadContract(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.contractService.getContract(this.contractId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contract) => {
          this.contract = contract;
          this.isLoading = false;
          this.loadPreview();
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.message || 'Failed to load contract';
          console.error('Failed to load contract:', error);
        }
      });
  }

  loadPreview(): void {
    this.isPreviewLoading = true;
    this.previewError = '';
    this.mammothHtml = null;
    this.mammothMessages = [];

    // Revoke any previous PDF blob URL
    if (this._pdfObjectUrl) {
      URL.revokeObjectURL(this._pdfObjectUrl);
      this._pdfObjectUrl = null;
      this.pdfBlobUrl = null;
    }

    // Determine file type from mimeType or filename extension
    const name     = this.contract?.name || '';
    const mime     = this.contract?.mimeType || '';
    const isPdf    = mime === 'application/pdf' || name.toLowerCase().endsWith('.pdf');
    const isImage  = mime.startsWith('image/') ||
                     /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);

    if (isPdf || isImage) {
      // ── PDF / IMAGE path ─────────────────────────────────────────────────
      // Download raw bytes from Alfresco and create a local blob URL for the iframe.
      // We use the 'docx' download route here but the backend just proxies
      // the raw Alfresco content regardless of extension — the blob type is set
      // from what the server returns, not the URL.
      this.contractService.downloadContract(this.contractId, 'docx')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (blob) => {
            const contentType = isPdf ? 'application/pdf' : (mime || 'image/png');
            const typedBlob   = new Blob([blob], { type: contentType });
            const objectUrl   = URL.createObjectURL(typedBlob);
            this._pdfObjectUrl = objectUrl;
            this.pdfBlobUrl    = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
            this.isPreviewLoading = false;
          },
          error: (error) => {
            this.isPreviewLoading = false;
            this.previewError = 'Could not load preview — download to view.';
            console.error('Failed to fetch PDF/image for preview:', error);
          }
        });

    } else {
      // ── DOCX path — mammoth.js ───────────────────────────────────────────
      this.contractService.downloadContract(this.contractId, 'docx')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async (blob) => {
            try {
              const arrayBuffer = await blob.arrayBuffer();
              const result = await mammoth.convertToHtml(
                { arrayBuffer },
                {
                  convertImage: mammoth.images.imgElement(image => {
                    return image.read('base64').then(base64 => ({
                      src: `data:${image.contentType};base64,${base64}`
                    }));
                  })
                }
              );
              if (result.messages?.length) {
                this.mammothMessages = result.messages
                  .filter(m => m.type === 'warning')
                  .map(m => m.message);
              }
              this.mammothHtml = this.sanitizer.bypassSecurityTrustHtml(result.value);
              this.isPreviewLoading = false;
            } catch (conversionError) {
              console.error('Mammoth conversion error:', conversionError);
              this.isPreviewLoading = false;
              this.previewError = 'Could not render document — download to view.';
            }
          },
          error: (error) => {
            this.isPreviewLoading = false;
            this.previewError = 'Preview not available';
            console.error('Failed to fetch DOCX for preview:', error);
          }
        });
    }
  }

  retry(): void { this.loadContract(); }

  // ========== ACTIONS ==========

  goBack(): void { this.router.navigate(['/contracts']); }

  editContract(): void { this.router.navigate(['/contract-wizard', this.contractId]); }

  downloadDocx(): void {
    this.contractService.downloadContract(this.contractId, 'docx')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.contract?.name || 'contract.docx'),
        error: () => alert('Failed to download document')
      });
  }

  downloadPdf(): void {
    this.contractService.downloadContract(this.contractId, 'pdf')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const pdfName = (this.contract?.name || 'contract').replace(/\.(docx|doc)$/i, '.pdf');
          this.downloadBlob(blob, pdfName);
        },
        error: () => {
          alert('PDF conversion not available. Downloading DOCX instead.');
          this.downloadDocx();
        }
      });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  openInNewTab(): void {
    window.open(this.contractService.getContractViewUrl(this.contractId), '_blank');
  }

  printContract(): void {
    const printContent = document.querySelector('.docx-viewer') as HTMLElement;
    if (printContent) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html><html>
          <head><title>${this.contract?.contractTitle || 'Contract'}</title>
          <style>body{font-family:'Times New Roman',serif;font-size:12pt;margin:2cm;color:#000}
          table{border-collapse:collapse;width:100%}td,th{border:1px solid #000;padding:6px}
          img{max-width:100%}@page{margin:2cm}</style></head>
          <body>${printContent.innerHTML}</body>
        </html>`);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
    } else {
      alert('To print, please download the document and print from Word/LibreOffice.');
      this.downloadDocx();
    }
  }

  toggleDownloadMenu(event: Event): void {
    event.stopPropagation();
    this.showDownloadMenu = !this.showDownloadMenu;
  }

  openInOffice365(): void {
    window.open(`${this.alfrescoBaseUrl}/s/api/office/edit/node/workspace/SpacesStore/${this.contractId}`, '_blank', 'noopener');
  }

  openInOnlyOffice(): void {
    window.open(`${this.alfrescoBaseUrl}/proxy/alfresco/onlyoffice/edit?nodeRef=workspace://SpacesStore/${this.contractId}`, '_blank', 'noopener');
  }

  // ========== WORKFLOW ==========

  /**
   * Start the review workflow.
   *
   * CHANGES (v2.1):
   *   - isStartingWorkflow flag prevents accidental double-submit
   *   - On SUCCESS: show green toast for 3 s, then navigate to /contracts
   *   - On ERROR: show red inline error banner, stay on page
   *   - workflowErrorMessage shown in the template next to the confirm dialog
   */
  startReviewWorkflow(): void {
    if (this.isStartingWorkflow) return;    // prevent double-click
    this.showWorkflowConfirm = false;
    this.workflowSuccessMessage = '';
    this.workflowErrorMessage = '';

    // Reviewers and approvers are saved to the contract node by the wizard
    // (skydoc:reviewers / skydoc:approvers) and come back via getContract().
    const reviewers: any[] = this.contract?.reviewers || [];
    const approvers: any[] = this.contract?.approvers || [];

    if (!reviewers.length) {
      this.workflowErrorMessage =
        'No reviewers assigned to this contract. Edit the contract and add reviewers in Step 4 before starting the workflow.';
      return;
    }
    if (!approvers.length) {
      this.workflowErrorMessage =
        'No approvers assigned to this contract. Edit the contract and add approvers in Step 4 before starting the workflow.';
      return;
    }

    this.isStartingWorkflow = true;

    const request = {
      contractNodeId: this.contractId,
      contractNumber: this.contract?.contractNumber || null,
      contractTitle:  this.contract?.contractTitle  || this.contract?.name || '',
      supplierName:   this.contract?.supplierName   || '',
      initiatorId:    this.authService.getEcmUsername() || '',  // Alfresco username from Keycloak JWT
      initiatorEmail: '',   // resolved server-side from JWT claims
      initiatorName:  '',   // resolved server-side from JWT claims
      reviewers,
      approvers
    };

    this.workflowService.startWorkflow(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isStartingWorkflow = false;
          this.contract.contractStatus = 'Pending Review';

          // Show success toast then navigate back to the contracts list after 2.5 s
          this.workflowSuccessMessage = `Workflow started for ${this.contract.contractNumber || 'this contract'}. Redirecting…`;
          console.log('[ContractDetails] Workflow started successfully:', response);

          setTimeout(() => {
            this.workflowSuccessMessage = '';
            this.router.navigate(['/contracts']);
          }, 1000);
        },
        error: (err) => {
          this.isStartingWorkflow = false;
          this.workflowErrorMessage = err?.message || 'Failed to start workflow. Please try again.';
          console.error('[ContractDetails] Failed to start workflow:', err);
        }
      });
  }

  dismissWorkflowError(): void {
    this.workflowErrorMessage = '';
  }

  // ========== EDIT PROPERTIES ==========

  startEditProperties(): void {
    this.editableContract = {
      contractTitle:  this.contract.contractTitle,
      contractType:   this.contract.contractType,
      contractStatus: this.contract.contractStatus,
      priority:       this.contract.priority,
      supplierName:   this.contract.supplierName,
      departmentName: this.contract.departmentName,
      contractAmount: this.contract.contractAmount,
      currency:       this.contract.currency || 'KES',
      effectiveDate:  this.toDateInputValue(this.contract.effectiveDate),
      expiryDate:     this.toDateInputValue(this.contract.expiryDate),
    };
    this.saveError = '';
    this.isEditingProperties = true;
  }

  cancelEditProperties(): void {
    this.isEditingProperties = false;
    this.saveError = '';
  }

  saveProperties(): void {
    this.isSavingProperties = true;
    this.saveError = '';

    const payload: any = {
      contractTitle:  this.editableContract.contractTitle,
      contractType:   this.editableContract.contractType,
      contractStatus: this.editableContract.contractStatus,
      priority:       this.editableContract.priority,
      supplierName:   this.editableContract.supplierName,
      departmentName: this.editableContract.departmentName,
      contractAmount: this.editableContract.contractAmount
                        ? Number(this.editableContract.contractAmount) : undefined,
      currency:       this.editableContract.currency,
      effectiveDate:  this.editableContract.effectiveDate || undefined,
      expiryDate:     this.editableContract.expiryDate    || undefined,
    };

    this.contractService.updateContractMetadata(this.contractId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSavingProperties = false;
          Object.assign(this.contract, {
            contractTitle:  payload.contractTitle,
            contractType:   payload.contractType,
            contractStatus: payload.contractStatus,
            priority:       payload.priority,
            supplierName:   payload.supplierName,
            departmentName: payload.departmentName,
            contractAmount: payload.contractAmount,
            currency:       payload.currency,
            effectiveDate:  payload.effectiveDate,
            expiryDate:     payload.expiryDate
          });
          this.isEditingProperties = false;
        },
        error: (err) => {
          this.isSavingProperties = false;
          this.saveError = err.message || 'Save failed — please try again.';
        }
      });
  }

  private toDateInputValue(date: string): string {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0];
  }

  // ========== FILE TYPE HELPERS ==========

  /** True when the contract file is a Word/DOCX document */
  get isDocxFile(): boolean {
    return /\.(docx?|odt)$/i.test(this.contract?.name || '');
  }

  /** True when the contract file is a PDF */
  get isPdfFile(): boolean {
    return /\.pdf$/i.test(this.contract?.name || '');
  }

  /**
   * Only Draft contracts that entered via the normal workflow path should show
   * the Start Workflow button.  EXECUTED contracts (uploaded already signed) are
   * already Active — they never need the review BPMN.
   */
  get isDraftContract(): boolean {
    return this.contract?.contractStatus === 'Draft'
        && this.contract?.contractOrigin !== 'EXECUTED';
  }

  // ========== ZOOM CONTROLS ==========

  zoomIn():    void { this.zoomLevel = Math.min(this.zoomLevel + 0.25, 3); }
  zoomOut():   void { this.zoomLevel = Math.max(this.zoomLevel - 0.25, 0.5); }
  resetZoom(): void { this.zoomLevel = 1; }

  // ========== UTILITY METHODS ==========

  private handleDocumentClick = (event: Event): void => {
    const target = event.target as HTMLElement;
    if (!target.closest('.download-dropdown-wrap')) this.showDownloadMenu = false;
  };

  getStatusClass(status: string): string {
    const statusMap: Record<string, string> = {
      'Draft': 'status-draft', 'Pending Review': 'status-pending', 'Active': 'status-active',
      'Expiring Soon': 'status-warning', 'Expired': 'status-expired', 'Terminated': 'status-terminated'
    };
    return statusMap[status] || 'status-draft';
  }

  getPriorityClass(priority: string): string {
    const priorityMap: Record<string, string> = {
      'Critical': 'priority-critical', 'High': 'priority-high',
      'Medium': 'priority-medium', 'Low': 'priority-low'
    };
    return priorityMap[priority] || 'priority-medium';
  }

  formatCurrency(amount: number, currency: string = 'KES'): string {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency', currency: currency || 'KES',
      minimumFractionDigits: 0
    }).format(amount);
  }

  formatDate(date: string): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  formatDateTime(date: string): string {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  formatPropertyLabel(key: string): string {
    if (!key) return '';
    let label = key.replace(/^[a-z]+:/i, '');
    label = label.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ');
    return label.trim().split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }

  calculateDaysRemaining(): number {
    if (!this.contract?.expiryDate) return 0;
    const diff = new Date(this.contract.expiryDate).getTime() - new Date().getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  getDaysRemainingClass(): string {
    const days = this.calculateDaysRemaining();
    if (days < 0) return 'days-expired';
    if (days <= 30) return 'days-critical';
    if (days <= 60) return 'days-warning';
    return 'days-normal';
  }

  isExpiringSoon(): boolean { const d = this.calculateDaysRemaining(); return d > 0 && d <= 30; }
  isExpired():      boolean { return this.calculateDaysRemaining() < 0; }

  // ========== INVOICE TAB ==========

  switchTab(tab: 'document' | 'invoices'): void {
    this.activeTab = tab;
    if (tab === 'invoices' && !this.invoices.length && !this.isLoadingInvoices) {
      this.loadInvoices();
    }
  }

  loadInvoices(): void {
    if (!this.contractId) return;
    this.isLoadingInvoices = true;
    this.invoiceError = '';

    this.invoiceService.getInvoices(this.contractId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (invoices) => { this.invoices = invoices; },
        error: (e) => { this.invoiceError = e.message; }
      });

    this.invoiceService.getInvoiceSummary(this.contractId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (summary) => { this.invoiceSummary = summary; this.isLoadingInvoices = false; },
        error: (e) => { this.invoiceError = e.message; this.isLoadingInvoices = false; }
      });
  }

  openAddInvoice(): void {
    this.editingInvoiceId = null;
    this.invoiceForm = {
      invoiceNumber: '', invoiceDate: '', invoiceAmount: null,
      currency: 'KES', description: '',
      supplierName: this.contract?.supplierName || '',
      poReference: '', invoiceStatus: 'PENDING',
    };
    this.pendingInvoiceFile = null;
    this.invoiceFormError = '';
    this.showAddInvoice = true;
  }

  openEditInvoice(inv: InvoiceResponse): void {
    this.editingInvoiceId = inv.id;
    this.invoiceForm = {
      invoiceNumber:  inv.invoiceNumber,
      invoiceDate:    inv.invoiceDate,
      invoiceAmount:  inv.invoiceAmount,
      currency:       inv.currency,
      description:    inv.description || '',
      supplierName:   inv.supplierName || '',
      poReference:    inv.poReference  || '',
      invoiceStatus:  inv.invoiceStatus,
    };
    this.pendingInvoiceFile = null;
    this.invoiceFormError = '';
    this.showAddInvoice = true;
  }

  closeInvoiceDialog(): void { this.showAddInvoice = false; }

  onInvoiceFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) this.pendingInvoiceFile = input.files[0];
  }

  saveInvoice(): void {
    if (!this.invoiceForm.invoiceNumber || !this.invoiceForm.invoiceDate || !this.invoiceForm.invoiceAmount) {
      this.invoiceFormError = 'Invoice Number, Date, and Amount are required.';
      return;
    }
    this.isSavingInvoice = true;
    this.invoiceFormError = '';

    const save$ = this.editingInvoiceId
      ? this.invoiceService.updateInvoice(this.contractId, this.editingInvoiceId, this.invoiceForm)
      : this.invoiceService.createInvoice(this.contractId, this.invoiceForm);

    save$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (saved) => {
        if (this.pendingInvoiceFile) {
          // ── File selected: attach THEN reload so hasDocument=true is visible immediately ──
          this.invoiceService.attachDocument(this.contractId, saved.id, this.pendingInvoiceFile)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                // Attach succeeded — now reload so download button appears straight away
                this.showAddInvoice   = false;
                this.isSavingInvoice  = false;
                this.pendingInvoiceFile = null;
                this.loadInvoices();
              },
              error: (e) => {
                // Attach failed — invoice record is saved, just warn and reload without the file
                console.warn('File attach failed (invoice record saved without document):', e);
                this.showAddInvoice   = false;
                this.isSavingInvoice  = false;
                this.pendingInvoiceFile = null;
                this.loadInvoices();
              }
            });
        } else {
          // ── No file: close and reload immediately ────────────────────────────────────
          this.showAddInvoice   = false;
          this.isSavingInvoice  = false;
          this.loadInvoices();
        }
      },
      error: (e) => {
        this.invoiceFormError = e.message || 'Failed to save invoice.';
        this.isSavingInvoice = false;
      }
    });
  }

  changeInvoiceStatus(inv: InvoiceResponse, status: 'PENDING' | 'APPROVED' | 'PAID' | 'REJECTED'): void {
    this.invoiceService.updateInvoiceStatus(this.contractId, inv.id, status)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: () => this.loadInvoices(), error: (e) => console.error(e) });
  }

  deleteInvoice(inv: InvoiceResponse): void {
    if (!confirm(`Delete invoice "${inv.invoiceNumber}"? This cannot be undone.`)) return;
    this.invoiceService.deleteInvoice(this.contractId, inv.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: () => this.loadInvoices(), error: (e) => console.error(e) });
  }

  downloadInvoiceDoc(inv: InvoiceResponse): void {
    this.invoiceService.downloadInvoiceDocument(this.contractId, inv.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => this.invoiceService.downloadBlob(blob, inv.invoiceFileName || `invoice-${inv.id}`),
        error: (e) => console.error('Download failed:', e)
      });
  }

  /**
   * Open the invoice document in a new browser tab using a blob URL.
   * PDFs render inline; other file types fall back to browser download.
   */
  viewInvoiceDoc(inv: InvoiceResponse): void {
    this.invoiceService.downloadInvoiceDocument(this.contractId, inv.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          // Detect MIME from filename if blob type is generic
          let mimeType = blob.type;
          if (!mimeType || mimeType === 'application/octet-stream') {
            const name = (inv.invoiceFileName || '').toLowerCase();
            if (name.endsWith('.pdf'))  mimeType = 'application/pdf';
            else if (name.endsWith('.png'))  mimeType = 'image/png';
            else if (name.endsWith('.jpg') || name.endsWith('.jpeg')) mimeType = 'image/jpeg';
          }
          const typedBlob = new Blob([blob], { type: mimeType });
          const url = URL.createObjectURL(typedBlob);
          window.open(url, '_blank');
          // Revoke after a short delay to let the new tab load
          setTimeout(() => URL.revokeObjectURL(url), 10000);
        },
        error: (e) => console.error('View failed:', e)
      });
  }

  

  getInvoiceStatusClass(status: string): string {
    const map: Record<string, string> = {
      PENDING:  'badge-pending',
      APPROVED: 'badge-approved',
      PAID:     'badge-paid',
      REJECTED: 'badge-rejected',
    };
    return map[status] || 'badge-pending';
  }

  getBalanceClass(): string {
    if (!this.invoiceSummary || this.invoiceSummary.contractAmountMissing) return '';
    const pct = this.invoiceSummary.percentageInvoiced;
    if (pct >= 100) return 'balance-over';
    if (pct >= 80)  return 'balance-warn';
    return 'balance-ok';
  }
}